import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientDto, PatientFormData } from "../types/patient";
import { patientService } from "../services/patientService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export interface PatientSearchParams {
  lastName?: string;
  firstName?: string;
  amka?: string;
}

type UsePatientsParam = string | number | PatientSearchParams;

export const usePatients = (param?: UsePatientsParam) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();

  // Διαχωρισμός αν η παράμετρος είναι ID ή αντικείμενο φίλτρων αναζήτησης
  const isSearchObject = typeof param === "object" && param !== null;
  const searchParams: PatientSearchParams | undefined = isSearchObject
    ? (param as PatientSearchParams)
    : undefined;

  const overridePatientId = !isSearchObject
    ? (param as string | number | undefined)
    : undefined;

  const patientId = overridePatientId ?? urlPatientId;

  // Έλεγχος αν υπάρχουν επαρκή κριτήρια για αναζήτηση
  const hasMinSearchChars =
    !!searchParams &&
    ((searchParams.lastName?.trim().length ?? 0) >= 2 ||
      (searchParams.firstName?.trim().length ?? 0) >= 2 ||
      (searchParams.amka?.trim().length ?? 0) >= 2);

  // Δημιουργία δυναμικού Cache Key για το useApi
  const searchKey = searchParams
    ? `patients-search-${searchParams.lastName ?? ""}-${searchParams.firstName ?? ""}-${searchParams.amka ?? ""}`
    : patientId
    ? "patients-none"
    : "patients-all";

  // 1. GET Ασθενών (Όλοι ή με φίλτρα αναζήτησης)
  const {
    data: patients = [],
    loading: isFetchingAll,
    error: getAllError,
    refetch: fetchPatients,
    invalidateCache: invalidateAllCache,
  } = useApi<PatientDto[]>(
    searchKey,
    async (signal) => {
      // Αν έχουμε patientId (είμαστε σε σελίδα ενός ασθενή), δεν φέρνουμε λίστα
      if (patientId) return [];

      // Αν περάστηκαν φίλτρα αλλά δεν συμπληρώθηκαν 2+ χαρακτήρες, μην κάνεις API call
      if (searchParams && !hasMinSearchChars) return [];

      // Κλήση του service περνώντας τα φίλτρα αναζήτησης
      return patientService.getAll(searchParams || {}, signal);
    },
    [
      patientId,
      searchParams?.lastName,
      searchParams?.firstName,
      searchParams?.amka,
    ]
  );

  // 2. GET ΕΝΑΣ ασθενής αποκλειστικά (με βάση το patientId)
  const {
    data: patient = null,
    loading: isFetchingOne,
    error: getOneError,
    refetch: fetchSinglePatient,
    invalidateCache: invalidateOneCache,
  } = useApi<PatientDto | null>(
    patientId ? `patient-${patientId}` : "patient-none",
    async (signal) => {
      if (!patientId) return null;
      return patientService.getById(patientId, signal);
    },
    [patientId]
  );

  // 3. CREATE mutation
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newPatient: PatientFormData) =>
    patientService.create(newPatient)
  );

  // 4. UPDATE mutation
  const {
    execute: updatePatientExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(
    async ({ id, data }: { id: number | string; data: PatientFormData }) => {
      return patientService.update(id, data as any);
    }
  );

  // 5. DELETE mutation
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatient = async (newPatient: PatientFormData): Promise<void> => {
    try {
      await createExecution(newPatient);
      invalidateAllCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Ο ασθενής αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      fetchPatients(true);
    } catch (err: any) {
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: err.message || "Σφάλμα δημιουργίας",
        type: "error",
      });
    }
  };

  const updatePatient = async (
    id: number | string,
    updatedData: PatientFormData
  ): Promise<void> => {
    try {
      await updatePatientExecution({ id, data: updatedData });
      invalidateAllCache();
      invalidateOneCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση των στοιχείων του ασθενούς ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchSinglePatient(true);
      fetchPatients(true);
    } catch (err: any) {
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: err.message || "Σφάλμα ενημέρωσης ασθενούς",
        type: "error",
      });
    }
  };

  const deletePatients = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateAllCache();
      invalidateOneCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchPatients(true);
    } catch (err: any) {
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: err.message || "Σφάλμα κατά τη μαζική διαγραφή",
        type: "error",
      });
    }
  };

  return {
    patients,
    patient,
    isLoading:
      isFetchingAll || isFetchingOne || isCreating || isDeleting || isUpdating,
    error:
      getAllError || getOneError || createError || deleteError || updateError,
    fetchPatients: () => fetchPatients(true),
    fetchSinglePatient: () => fetchSinglePatient(true),
    createPatient,
    updatePatient,
    deletePatients,
    modalState,
    closeModal,
  };
};